import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { DollarSign, Save, Loader2 } from "lucide-react";
import { api, apiError, money, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export default function AdminMinCache() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/config/min-cache")
      .then(({ data }) => { setRows(data.instruments || []); setMeta({ updated_at: data.updated_at, updated_by: data.updated_by }); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRow = (inst, k, v) => setRows((p) => p.map((r) => (r.instrument === inst ? { ...r, [k]: v } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const items = {};
      rows.forEach((r) => { items[r.instrument] = { min: parseFloat(r.min) || 0, enabled: !!r.enabled }; });
      const { data } = await api.put("/admin/config/min-cache", { items });
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Cachê mínimo por instrumento atualizado.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-zinc-500 py-10 text-center" data-testid="min-cache-loading">Carregando...</div>;

  return (
    <div className="space-y-5" data-testid="admin-min-cache">
      <div className="flex items-start gap-3 p-4 rounded-sm border border-[#00F0FF]/25 bg-[#00F0FF]/5">
        <DollarSign className="h-5 w-5 text-[#00F0FF] mt-0.5 shrink-0" />
        <div className="text-sm text-zinc-300">
          Defina o <strong>cachê mínimo</strong> aceito por instrumento/função. Quando ativa, a regra impede a
          publicação de eventos com cachê abaixo do valor — em criação, edição, duplicação, modelos e eventos recorrentes.
          A alteração vale imediatamente para novos eventos e eventos ainda não publicados; eventos concluídos não mudam.
        </div>
      </div>

      <div className="rounded-sm border border-border overflow-hidden">
        <div className="grid grid-cols-[1fr_180px_120px] gap-2 px-4 py-2.5 bg-[#121217] text-[10px] uppercase tracking-widest text-zinc-500">
          <span>Instrumento / Função</span><span>Cachê mínimo (R$)</span><span className="text-right">Regra ativa</span>
        </div>
        {rows.map((r) => (
          <div key={r.instrument} data-testid={`min-cache-row-${r.instrument}`} className="grid grid-cols-[1fr_180px_120px] gap-2 items-center px-4 py-2.5 border-t border-border">
            <span className="text-sm text-zinc-200">{r.instrument}</span>
            <Input data-testid={`min-cache-input-${r.instrument}`} type="number" min="0" step="0.01" value={r.min}
              onChange={(e) => setRow(r.instrument, "min", e.target.value)} placeholder="0,00"
              className="bg-[#09090B] border-border rounded-sm h-9 w-40" />
            <div className="flex justify-end">
              <Switch data-testid={`min-cache-enabled-${r.instrument}`} checked={!!r.enabled} onCheckedChange={(v) => setRow(r.instrument, "enabled", v)} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs text-zinc-500">
          {meta.updated_at ? <>Última alteração: {formatDateTime(meta.updated_at)}{meta.updated_by ? ` · por ${meta.updated_by}` : ""}</> : "Ainda não configurado."}
        </span>
        <Button data-testid="min-cache-save-btn" onClick={save} disabled={saving} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvar configuração
        </Button>
      </div>
    </div>
  );
}
